import { useEffect, useRef, useState, useCallback } from 'react'
import { HocuspocusProvider } from '@hocuspocus/provider'
import classNames from 'classnames'
import { useSetRecoilState } from 'recoil'
import * as Y from 'yjs'

import { AVATAR_COLORS } from 'consts/dashboardAvatarColors'
import { useAuth } from 'contexts/AuthContext'
import { useDebounce } from 'hooks'
import { dashboardTheme } from 'recoil/atoms'
import { UserStore } from 'stores'
import { analyticsTracker } from 'utils'
import getEnvFromLocation from 'utils/getEnvFromLocation'
import { DEFAULT_JOB_THEME } from 'utils/jobThemeUtils'

import { RealTimeContext } from './context'
import './index.scss'
import { DashBuilderStatus } from './types'

const socketProviderURLs = {
  devHocuspocus: 'wss://yjs-hocuspocus.dev.insights.winnin.com',
  prodHocuspocus: 'wss://yjs-hocuspocus.insights.winnin.com',
}

const closeProvider = provider => {
  provider?.disconnect()
  provider?.destroy()
  provider?.configuration?.websocketProvider?.disconnect()
  provider?.configuration?.websocketProvider?.destroy()
}

function ContentWrapper({ isSynced, children }) {
  return <div className={classNames({ 'RealTimeContext-noInteract': isSynced === false })}>{children}</div>
}

export function RealTimeContextProvider({ children, dashboardId }) {
  const [isHocusPocusConnected, setIsHocusPocusConnected] = useState(false)
  const [isHocusPocusAuthenticated, setIsHocusPocusAuthenticated] = useState(false)
  const [connectedUsers, setConnectedUsers] = useState([])
  const [dashBuilderStatus, setDashBuilderStatus] = useState(DashBuilderStatus.Default)

  const setDashboardTheme = useSetRecoilState(dashboardTheme)

  const yDocRef = useRef(null)
  const dashMapRef = useRef(null)
  const dashMapSectionsRef = useRef(null)
  const wsProvider = useRef(null)
  const undoManagerComponents = useRef(null)

  const auth = useAuth()

  useEffect(() => {
    const yDoc = new Y.Doc()
    yDocRef.current = yDoc

    const yDashboardThemeMap = yDocRef.current?.getMap('jobDashboardTheme')

    function handleThemeChange(event) {
      const yTargetMap = event.target

      if (!event.transaction.local || event.transaction.origin instanceof Y.UndoManager) {
        const updatedTheme = [...yTargetMap.values()][0]
        if (updatedTheme) {
          setDashboardTheme(updatedTheme)
        }
      }
    }

    yDashboardThemeMap?.observe(handleThemeChange)

    dashMapRef.current = yDoc.getMap('dashboard')
    dashMapSectionsRef.current = yDoc.getMap('sections')

    const dashboardHeadersYmap = yDocRef.current?.getMap('dashboardHeaders')

    dashboardHeadersYmap.observe(event => {
      const value = event.target.toJSON()
      setDashBuilderStatus(value?.dashBuilderStatus)
    })

    undoManagerComponents.current = new Y.UndoManager([dashMapRef.current], {
      captureTimeout: 1000,
    })

    // can be uncommented for debug purposes
    // undoManagerComponents.current.on('stack-item-added', (event) => {
    //   // save the current cursor location on the stack-item
    //   // event.stackItem.meta.set('cursor-location', getRelativeCursorLocation())
    //   console.log('undoManager stack-item-added', event)
    // })
    // undoManagerComponents.current.on('stack-item-popped', (event) => {
    //   // restore the current cursor location on the stack-item
    //   // restoreCursorLocation(event.stackItem.meta.get('cursor-location'))
    //   console.log('undoManager stack-item-popped', event)
    // })

    initializeWebSockets(auth?.keycloak?.token, dashboardId, yDocRef, setIsHocusPocusConnected)

    return () => {
      closeProvider(wsProvider.current)
      setDashboardTheme(DEFAULT_JOB_THEME)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId])

  useEffect(() => {
    const handleKeyDown = event => {
      const code = event.which || event.keyCode
      const charCode = String.fromCharCode(code).toLowerCase()
      const regularCtrl = event.ctrlKey || event.metaKey
      const ctrlShift = regularCtrl && event.shiftKey
      const isCtrlPressed = regularCtrl || ctrlShift

      if (isCtrlPressed && charCode === 'z') {
        undoManagerComponents.current.undo()
      } else if (isCtrlPressed && charCode === 'y') {
        undoManagerComponents.current.redo()
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  function initializeWebSockets(token, dashboardId, ydocRef, setIsHocusPocusConnected) {
    const env = getEnvFromLocation()
    const isProduction = ['staging', 'production'].includes(env)

    setIsHocusPocusConnected(false)
    closeProvider(wsProvider.current)
    wsProvider.current = new HocuspocusProvider({
      url: isProduction ? socketProviderURLs.prodHocuspocus : socketProviderURLs.devHocuspocus,
      name: dashboardId,
      document: ydocRef.current,
      token,
      onAuthenticated: () => {
        console.log('authenticated')
        setIsHocusPocusAuthenticated(true)
      },
      onAuthenticationFailed: ({ reason }) => {
        console.log('authentication failed', reason)
        setIsHocusPocusAuthenticated(false)
        refreshSocketProvider()
      },
      onSynced: ({ state }) => {
        console.log('sync', state)
        setIsHocusPocusConnected(state)
      },
      onConnect() {
        console.log('connect')
      },
      onDisconnect: ({ event }) => {
        console.log('disconnect', event)
      },
      onClose({ event }) {
        console.log('close', event)
        setIsHocusPocusConnected(false)
        if (event.reason === 'locked') {
          window.location.reload()
        }
      },
      onAwarenessChange: () => {
        const awarenessStates = Array.from(wsProvider.current.awareness.getStates().values())
        let awarenessConnectedUsers = awarenessStates.map(state => (state as { user: any }).user)
        awarenessConnectedUsers = awarenessConnectedUsers.filter(
          (user, index, self) =>
            index ===
            self.findIndex(u => {
              return u?.id === user?.id
            })
        ) // deduplicate users by id (an user might be connected in several tabs or browsers)
        const usersWithColors = awarenessConnectedUsers.map((item, index) => {
          const modifier = Math.ceil(index / 8)
          const colorIndex = index > 8 ? 8 * modifier - index : index

          return {
            ...item,
            color: AVATAR_COLORS?.[colorIndex],
          }
        })
        setConnectedUsers(usersWithColors)
      },
    })
    wsProvider.current.awareness.setLocalStateField('user', {
      id: UserStore.userInfo.id,
      name: UserStore.userInfo.name,
      thumbnail: UserStore.userInfo.thumbnail,
    })
  }

  async function refreshSocketProvider() {
    console.log('refreshSocketProvider')

    await auth?.keycloak?.updateToken(60)
    const token = auth?.keycloak?.token

    initializeWebSockets(token, dashboardId, yDocRef, setIsHocusPocusConnected)
  }

  const sendStateless = useCallback((operationType, operationPayload) => {
    const statelessMessage = JSON.stringify({
      operationType,
      operationPayload,
    })
    wsProvider.current.sendStateless(statelessMessage)
  }, [])

  /**
   *
   * @param {'undo' | 'redo'} action
   * @returns {void}
   */
  const handleUndoRedo = useCallback(
    action => {
      return () => {
        const { UNDO, REDO } = analyticsTracker.eventNames
        if (action === 'undo') {
          undoManagerComponents.current.undo()
          analyticsTracker.eventTrack(UNDO, {
            userId: UserStore.userInfo.id,
            dashboardId,
          })
          return
        }

        undoManagerComponents.current.redo()
        analyticsTracker.eventTrack(REDO, {
          userId: UserStore.userInfo.id,
          dashboardId,
        })
      }
    },
    [dashboardId]
  )

  const handleResetUndoManager = useCallback(() => {
    undoManagerComponents.current.clear()
  }, [])

  const updateDashboardTheme = useCallback(
    theme => {
      if (!yDocRef.current) return

      const yDashboardThemeMap = yDocRef.current.getMap('jobDashboardTheme')

      yDocRef.current.transact(() => {
        yDashboardThemeMap.set('jobDashboardTheme', theme)
      })

      setDashboardTheme(theme)
    },
    [setDashboardTheme]
  )

  const resetUser = () => {
    const baseUser = connectedUsers.find(item => item.id === UserStore.userInfo.id)

    wsProvider.current.awareness.setLocalStateField('user', {
      ...baseUser,
      subgridId: null,
    })
  }

  const { debouncedAction: debouncedresetUser } = useDebounce(resetUser)

  const updateUserState = state => {
    debouncedresetUser()

    const baseUser = connectedUsers.find(item => item.id === UserStore.userInfo.id)

    wsProvider.current.awareness.setLocalStateField('user', {
      ...baseUser,
      ...state,
    })
  }

  return (
    <RealTimeContext.Provider
      value={{
        yDocRef,
        isHocusPocusConnected,
        dashMapRef,
        dashMapSectionsRef,
        updateDashboardTheme,
        sendStateless,
        connectedUsers,
        handleUndoRedo,
        resetUndoManager: handleResetUndoManager,
        updateUserState,
        dashBuilderStatus,
      }}
    >
      <ContentWrapper isSynced={isHocusPocusConnected && isHocusPocusAuthenticated}>{children}</ContentWrapper>
    </RealTimeContext.Provider>
  )
}
